import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  LineChart, Line, ScatterChart, Scatter,
} from "recharts";

import { useLang, fmt, Stars } from '../App';
import { useQueue } from '../App';
import { computeSkills, computePPTrend } from '../utils/helpers';

/* ══════════════════════════════════════════
   РАСЧЁТ WEIGHTED PP
   
   Формула osu!:
   total_pp = Σ (pp[i] * 0.95^i) + bonus_pp
   
   Для отображения "сколько дала конкретная карта":
   Если карта попала в топ на позицию i:
     weighted = pp * 0.95^i
   Если не попала в топ — 0.
   
   Мы матчим recent plays → bestPlays по имени карты + диффу.
   Возвращает { weighted, raw } или null если не в топе.
══════════════════════════════════════════ */
function calcWeightedPP(play, bestPlays) {
  if (!play.pp || play.pp <= 0) return null;
  const idx = bestPlays.findIndex(
    b => b.map === play.map && b.diff === play.diff && b.pp === play.pp
  );
  if (idx === -1) return null;
  const weighted = Math.round(play.pp * Math.pow(0.95, idx));
  return { weighted, raw: play.pp, idx };
}

/* ══════════════════════════════════════════
   УТИЛИТА: группировка recent plays
   — фильтруем фейлы (rank === 'F')
   — лучшая попытка первой
══════════════════════════════════════════ */
function groupRecentPlays(plays, bestPlays) {
  const passed = plays.filter(p => p.rank !== 'F');

  const map = new Map();
  passed.forEach(play => {
    const key = play.map + '|' + play.diff;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(play);
  });

  const groups = [];
  map.forEach((attempts, key) => {
    const latestDate = attempts[0].date;

    const sorted = [...attempts].sort((a, b) =>
      b.acc !== a.acc ? b.acc - a.acc : b.pp - a.pp
    );

    const best     = sorted[0];
    const worst    = sorted[sorted.length - 1];
    const accDelta = +(best.acc - worst.acc).toFixed(1);
    const ppDelta  = best.pp - worst.pp;

    // Считаем weighted PP для лучшей попытки
    const ppInfo = calcWeightedPP(best, bestPlays);

    groups.push({
      mapName: best.map,
      diff:    best.diff,
      attempts: sorted,
      best,
      accDelta,
      ppDelta,
      ppInfo,
      count:      sorted.length,
      latestDate,
    });
  });

  return groups;
}

/* ══════════════════════════════════════════
   ЦВЕТНЫЕ ЧИПЫ МОДОВ
══════════════════════════════════════════ */
const MOD_COLORS = {
  HD: { bg: "rgba(251,191,36,0.18)",  border: "rgba(251,191,36,0.5)",  text: "#fbbf24" },
  DT: { bg: "rgba(167,139,250,0.18)", border: "rgba(167,139,250,0.5)", text: "#a78bfa" },
  NC: { bg: "rgba(192,132,252,0.18)", border: "rgba(192,132,252,0.5)", text: "#c084fc" },
  HR: { bg: "rgba(248,113,113,0.18)", border: "rgba(248,113,113,0.5)", text: "#f87171" },
  FL: { bg: "rgba(96,165,250,0.18)",  border: "rgba(96,165,250,0.5)",  text: "#60a5fa" },
  EZ: { bg: "rgba(74,222,128,0.18)",  border: "rgba(74,222,128,0.5)",  text: "#4ade80" },
  HT: { bg: "rgba(34,211,238,0.18)",  border: "rgba(34,211,238,0.5)",  text: "#22d3ee" },
  SO: { bg: "rgba(125,211,252,0.18)", border: "rgba(125,211,252,0.5)", text: "#7dd3fc" },
  SD: { bg: "rgba(251,146,60,0.18)",  border: "rgba(251,146,60,0.5)",  text: "#fb923c" },
  PF: { bg: "rgba(245,158,11,0.18)",  border: "rgba(245,158,11,0.5)",  text: "#f59e0b" },
  NM: { bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.25)", text: "#94a3b8" },
};

const ModChips = ({ mods, small = false }) => {
  if (!mods || mods.length === 0) return null;
  const list = Array.isArray(mods) ? mods : [mods];
  const filtered = list.filter(m => m && m !== 'NM');
  if (filtered.length === 0) return null;

  return (
    <span style={{ display: "inline-flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
      {filtered.map(mod => {
        const c = MOD_COLORS[mod] || MOD_COLORS.NM;
        return (
          <span key={mod} style={{
            fontSize: small ? 9 : 10, fontWeight: 800,
            padding: small ? "1px 4px" : "1px 6px",
            borderRadius: 5,
            border: `1px solid ${c.border}`,
            background: c.bg, color: c.text,
            fontFamily: "'Share Tech Mono', monospace", letterSpacing: 0.3,
          }}>{mod}</span>
        );
      })}
    </span>
  );
};

/* ══════════════════════════════════════════
   PP DISPLAY: weighted(raw) формат
   Если карта в топе — показываем 2(58)pp
   Иначе — просто 58pp серым (не влияет на топ)
══════════════════════════════════════════ */
const PPDisplay = ({ pp, ppInfo, size = 13 }) => {
  if (!pp || pp <= 0) return null;

  if (ppInfo) {
    // Карта в топе
    return (
      <span className="mono" style={{ fontSize: size, fontWeight: 700, color: "var(--a)", display: "inline-flex", alignItems: "baseline", gap: 1 }}>
        <span style={{ color: "var(--a)" }}>{ppInfo.weighted}</span>
        <span style={{ fontSize: size - 2, color: "rgba(186,140,255,0.7)", fontWeight: 600 }}>
          ({ppInfo.raw})
        </span>
        <span style={{ fontSize: size - 2, color: "var(--muted)", marginLeft: 1 }}>pp</span>
      </span>
    );
  }

  // Не в топе — показываем raw pp обычным цветом (не влияет на топ, но выглядит нормально)
  return (
    <span className="mono" style={{ fontSize: size, fontWeight: 700, color: "var(--a)" }}>
      {pp}pp
    </span>
  );
};

/* ══════════════════════════════════════════
   ATTEMPT ROW
   Rank | Acc | Δ | PP | Mods | Diff | Time
══════════════════════════════════════════ */
const ATTEMPT_GRID = "38px 54px 54px 110px 72px 1fr 64px";

const AttemptRow = ({ attempt, prevAttempt, isFirst, bestPlays }) => {
  const accDiff  = prevAttempt ? +(attempt.acc - prevAttempt.acc).toFixed(1) : null;
  const improved = accDiff !== null && accDiff > 0;
  const ppInfo   = calcWeightedPP(attempt, bestPlays);

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: ATTEMPT_GRID,
      alignItems: "center", gap: 6,
      padding: "6px 14px 6px 32px",
      background: isFirst ? "rgba(255,255,255,0.025)" : "transparent",
      borderBottom: "1px solid rgba(255,255,255,0.04)",
    }}>
      <span className={`rank-badge rank-${attempt.rank}`} style={{ fontSize: 9, padding: "1px 4px", justifySelf: "start" }}>
        {attempt.rank}
      </span>
      <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>
        {attempt.acc}%
      </span>
      <span style={{
        fontSize: 11, fontWeight: 700,
        color: accDiff === null ? "transparent"
             : improved           ? "#68d891"
             : accDiff < 0        ? "#f87171"
             : "var(--muted)",
      }}>
        {accDiff !== null ? (improved ? `↑ +${accDiff}%` : accDiff < 0 ? `↓ ${accDiff}%` : "→ 0%") : "—"}
      </span>
      <PPDisplay pp={attempt.pp} ppInfo={ppInfo} size={12} />
      <span><ModChips mods={attempt.mods} small /></span>
      <span style={{ fontSize: 11, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {attempt.diff || "—"}
      </span>
      <span style={{ fontSize: 10, color: "var(--muted)", textAlign: "right" }}>
        {attempt.date}
      </span>
    </div>
  );
};

/* ══════════════════════════════════════════
   PLAY GROUP CARD
══════════════════════════════════════════ */
const PlayGroup = ({ group, lang, isLast, bestPlays }) => {
  const [expanded, setExpanded] = useState(false);
  const { best, attempts, count, accDelta, latestDate, ppInfo } = group;

  const progressColor = accDelta > 1  ? "#68d891"
                      : accDelta > 0  ? "#7eb8f7"
                      : accDelta < 0  ? "#f87171"
                      : "var(--muted)";

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid var(--border)" }}>
      <div
        onClick={() => count > 1 && setExpanded(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "10px 0",
          cursor: count > 1 ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <span className={`rank-badge rank-${best.rank}`}>{best.rank}</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 600, fontSize: 13,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {best.map}
            {count > 1 && (
              <span style={{
                fontSize: 10, fontWeight: 800, padding: "1px 7px", borderRadius: 10,
                background: "var(--dim)", border: "1px solid var(--border)",
                color: "var(--a3)", flexShrink: 0,
              }}>×{count}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {best.diff && <span>{best.diff}</span>}
            {best.stars > 0 && <span style={{ color: "#ffb6d0" }}>✦ {best.stars}★</span>}
            <ModChips mods={best.mods} />
            {count > 1 && accDelta !== 0 && (
              <span style={{ color: progressColor, fontWeight: 700 }}>
                {accDelta > 0 ? `↑ +${accDelta}%` : `↓ ${accDelta}%`}
              </span>
            )}
          </div>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
            <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{best.acc}%</span>
            {/* PP в формате weighted(raw) */}
            <PPDisplay pp={best.pp} ppInfo={ppInfo} size={13} />
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1, display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
            <span>{latestDate}</span>
            {count > 1 && <span style={{ fontSize: 10, opacity: 0.5 }}>{expanded ? "▲" : "▼"}</span>}
          </div>
        </div>
      </div>

      {expanded && count > 1 && (
        <div style={{
          background: "var(--bg2)", borderRadius: "0 0 10px 10px",
          marginBottom: 6, border: "1px solid var(--border)", borderTop: "none",
          overflow: "hidden",
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: ATTEMPT_GRID,
            gap: 6,
            padding: "5px 14px 5px 32px",
            fontSize: 9, fontWeight: 800, textTransform: "uppercase",
            letterSpacing: 0.8, color: "var(--muted)",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}>
            <span>{lang === "ru" ? "РНГ" : "RNK"}</span>
            <span>{lang === "ru" ? "ТОЧН" : "ACC"}</span>
            <span>Δ</span>
            <span>PP</span>
            <span>{lang === "ru" ? "МОДЫ" : "MODS"}</span>
            <span>{lang === "ru" ? "СЛОЖНОСТЬ" : "DIFF"}</span>
            <span style={{ textAlign: "right" }}>{lang === "ru" ? "ВРЕМЯ" : "TIME"}</span>
          </div>
          {attempts.map((attempt, i) => (
            <AttemptRow
              key={i}
              attempt={attempt}
              prevAttempt={attempts[i + 1] || null}
              isFirst={i === 0}
              bestPlays={bestPlays}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════
   ACTIVITY CARD
══════════════════════════════════════════ */
const ActivityCard = ({ p, lang }) => {
  // ── Подсчёт за последние 24 часа ─────────────────────────
  const now = Date.now();
  const recent24 = (p.recentActivity || []).filter(play => {
    if (!play.timestamp) return true; // если нет timestamp — включаем (старые данные)
    return (now - new Date(play.timestamp).getTime()) < 86_400_000;
  });

  const totalPlays  = recent24.length;         // включая фейлы
  const passedPlays = recent24.filter(p => p.rank !== 'F').length;
  const failCount   = totalPlays - passedPlays;

  // PP дельта: текущий PP - снапшот 24ч назад
  // Если снапшота нет, но плеев тоже 0 — показываем 0 (ничего не сыграно = 0 прироста)
  const ppDelta = p.pp_24h_ago != null
    ? Math.round((p.pp || 0) - p.pp_24h_ago)
    : totalPlays === 0 ? 0 : null;

  return (
    <div className="card fu d2" style={{
      padding: '18px 20px', display: 'flex', flexDirection: 'column',
      justifyContent: 'center', gap: 14, minWidth: 140,
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="accent-text">⚡</span>
          {lang === 'ru' ? 'Активность' : 'Activity'}
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
          {lang === 'ru' ? 'за последние 24 часа' : 'last 24 hours'}
        </div>
      </div>

      <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>
          {lang === 'ru' ? 'Игр сыграно' : 'Plays'}
        </div>
        <div className="mono" style={{ fontSize: 28, fontWeight: 700, color: '#7eb8f7', lineHeight: 1 }}>
          {totalPlays}
        </div>
        {failCount > 0 && (
          <div style={{ fontSize: 10, color: '#f87171', marginTop: 3 }}>
            {failCount} {lang === 'ru' ? 'провалено' : 'failed'}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>
          {lang === 'ru' ? 'PP за 24ч' : 'PP gained'}
        </div>
        {ppDelta !== null ? (
          <div className="mono" style={{
            fontSize: 24, fontWeight: 700, lineHeight: 1,
            color: ppDelta > 0 ? '#68d891' : ppDelta < 0 ? '#f87171' : 'var(--muted)',
          }}>
            {ppDelta > 0 ? `+${ppDelta}` : ppDelta}
            <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 3 }}>pp</span>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--muted)', opacity: 0.6 }}>
            {lang === 'ru' ? 'Пока что не работает' : 'Not available yet'}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   QUEUE SECTION
══════════════════════════════════════════ */
const QueueSection = ({ lang }) => {
  const { queue, removeFromQueue, toggleDone } = useQueue();
  const [showDone, setShowDone] = useState(false);
  const pending = queue.filter(m => !m.done);
  const done    = queue.filter(m =>  m.done);
  const visible = showDone ? queue : pending;

  if (queue.length === 0) return null;

  return (
    <div className="card fu" style={{ padding: 22 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10, background: "var(--grad)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, color: "var(--bg)", boxShadow: "0 4px 12px var(--glow)",
          }}>♪</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {lang === "ru" ? "Очередь карт" : "Map Queue"}
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>
              {pending.length} {lang === "ru" ? "ожидает" : "pending"}
              {done.length > 0 && ` · ${done.length} ${lang === "ru" ? "пройдено" : "done"}`}
            </div>
          </div>
        </div>
        {done.length > 0 && (
          <button className="btn-ghost" onClick={() => setShowDone(v => !v)}
            style={{ padding: "4px 12px", fontSize: 11, borderRadius: 20 }}>
            {showDone
              ? (lang === "ru" ? "Скрыть пройденные" : "Hide done")
              : (lang === "ru" ? `Показать (${done.length})` : `Show done (${done.length})`)}
          </button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visible.map((m) => (
          <QueueItem key={m.id} m={m} lang={lang}
            onRemove={() => removeFromQueue(m.id)}
            onToggleDone={() => toggleDone(m.id)} />
        ))}
      </div>
      {pending.length === 0 && !showDone && (
        <div style={{ textAlign: "center", padding: "24px 0", color: "var(--muted)", fontSize: 13 }}>
          {lang === "ru" ? "🎉 Все карты пройдены!" : "🎉 All maps completed!"}
        </div>
      )}
    </div>
  );
};

const QueueItem = ({ m, lang, onRemove, onToggleDone }) => {
  const stars = +(m.stars || 0);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", borderRadius: 12,
      background: m.done ? "rgba(104,216,145,0.05)" : "var(--bg2)",
      border: `1px solid ${m.done ? "rgba(104,216,145,0.2)" : "var(--border)"}`,
      opacity: m.done ? 0.7 : 1, transition: "all 0.2s",
    }}>
      <button onClick={onToggleDone} style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
        border: `2px solid ${m.done ? "#68d891" : "var(--border)"}`,
        background: m.done ? "rgba(104,216,145,0.15)" : "transparent",
        color: "#68d891", fontSize: 13, fontWeight: 700,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>{m.done ? "✓" : ""}</button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 700, fontSize: 13,
          textDecoration: m.done ? "line-through" : "none",
          color: m.done ? "var(--muted)" : "var(--text)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{m.title}</div>
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {m.artist && <span>{m.artist}</span>}
          {stars > 0 && <span style={{ color: "#ffb6d0" }}>✦ {stars.toFixed(2)}★</span>}
          {m.bpm    && <span>BPM {m.bpm}</span>}
          {m.length && <span>⏱ {m.length}</span>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => window.open(`https://osu.ppy.sh/beatmapsets/${m.beatmapSetId || m.id}`, "_blank")}
          className="btn-ghost" style={{ padding: "4px 10px", fontSize: 11, borderRadius: 8 }}
        >↗ osu!</button>
        <button onClick={onRemove} style={{
          padding: "4px 10px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.3)",
          background: "rgba(248,113,113,0.08)", color: "#f87171",
          fontSize: 11, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
        }}>✕</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════ */
const Dashboard = ({ user, onRefresh, isRefreshing }) => {
  const [playsTab,       setPlaysTab]       = useState("recent");
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [bestExpanded,   setBestExpanded]   = useState(false);
  const { t, lang } = useLang();
  const dt = t.dash;

  const recentAll = user?.recentActivity || [];
  const bestAll   = user?.bestPlays      || [];

  // bestAll передаём в groupRecentPlays для расчёта weighted PP
  const recentGroups = useMemo(() => groupRecentPlays(recentAll, bestAll), [recentAll, bestAll]);
  const skills       = useMemo(() => computeSkills(user)  || [], [user]);
  const ppTrend      = useMemo(() => computePPTrend(user) || [], [user]);

  if (!user) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>Please login to see your stats</h2>
      </div>
    );
  }

  const p       = user;
  const initial = (p.username || "U").charAt(0).toUpperCase();

  const visibleGroups = recentExpanded ? recentGroups : recentGroups.slice(0, 3);
  const recentHasMore = recentGroups.length > 3;

  const visibleBest = bestExpanded ? bestAll : bestAll.slice(0, 3);
  const bestHasMore = bestAll.length > 3;

  const trackerDate = p.trackerJoinedAt
    ? new Date(p.trackerJoinedAt).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US", { year: "numeric", month: "short" })
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>

      {/* ── Hero Card ── */}
      <div className="card fu" style={{
        padding: "28px 32px", position: "relative", overflow: "hidden",
        background: "linear-gradient(135deg,var(--card) 0%,var(--card2) 100%)",
      }}>
        <div style={{
          position: "absolute", right: -60, top: -60, width: 300, height: 300,
          borderRadius: "50%", background: "radial-gradient(circle,var(--glow) 0%,transparent 65%)"
        }} />
        <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap", position: "relative" }}>
          <div style={{
            width: 80, height: 80, borderRadius: 22, flexShrink: 0,
            background: p.avatar_url ? `url(${p.avatar_url}) center/cover` : "var(--grad)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 32, color: "var(--bg)", fontWeight: 800,
            boxShadow: "0 8px 30px var(--glow)", border: "2px solid var(--border)", overflow: "hidden",
          }}>
            {!p.avatar_url && initial}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
              <h2 className="syne" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>
                <span className="accent-text">{p.username}</span>
              </h2>
              <span style={{ fontSize: 22, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}>
                {p.country_code === 'RU' ? '🇷🇺' : p.country_code === 'JP' ? '🇯🇵' : (p.country_code || '🏳️')}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {p.badges?.length > 0
                ? p.badges.map(b => <span key={b} className="tag">{b}</span>)
                : <span className="tag">Player</span>}
              {trackerDate && (
                <span style={{
                  fontSize: 10, padding: "2px 9px", borderRadius: 20,
                  background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.25)",
                  color: "#a78bfa", fontWeight: 600,
                }}>
                  ⊙ {lang === "ru" ? `В трекере с ${trackerDate}` : `Tracker since ${trackerDate}`}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="btn-ghost"
                style={{ padding: "8px 14px", fontSize: 12, borderRadius: 10, opacity: isRefreshing ? 0.6 : 1, whiteSpace: "nowrap" }}
              >
                {isRefreshing ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 12, height: 12, border: "2px solid var(--border)", borderTopColor: "var(--a)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    {lang === "ru" ? "Обновляю..." : "Refreshing..."}
                  </span>
                ) : (
                  `↻ ${lang === "ru" ? "Обновить" : "Refresh"}`
                )}
              </button>
            )}
            <div style={{ textAlign: "right" }}>
              <div className="mono" style={{ fontSize: 38, fontWeight: 700, color: "var(--a)", lineHeight: 1 }}>
                {fmt(Math.round(p.pp || 0))}
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Performance Points</div>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 12, marginTop: 24 }}>
          {[
            { l: lang === "ru" ? "Глоб. рейтинг"  : "Global Rank",   v: `#${fmt(p.global_rank  || p.globalRank  || 0)}`, icon: "🌍", c: "var(--a)"    },
            { l: lang === "ru" ? "Рейтинг страны" : "Country Rank",  v: `#${fmt(p.country_rank || p.countryRank || 0)}`, icon: "🏆", c: "var(--amber)"},
            { l: lang === "ru" ? "Точность"        : "Accuracy",      v: `${p.accuracy || 0}%`,                           icon: "◎",  c: "#68d891"     },
            { l: lang === "ru" ? "Игр"             : "Play Count",    v: fmt(p.play_count || p.playCount || 0),            icon: "▶",  c: "#7eb8f7"     },
            { l: lang === "ru" ? "Время"           : "Play Time",     v: p.play_time || p.playTime || '0h',               icon: "⏱",  c: "#c084fc"     },
            { l: lang === "ru" ? "Макс. комбо"     : "Max Combo",     v: `${fmt(p.max_combo || p.maxCombo || 0)}x`,       icon: "🔥", c: "#f87171"     },
          ].map((s, i) => (
            <div key={i} style={{ background: "var(--bg2)", borderRadius: 12, padding: "12px 14px", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{s.icon}</div>
              <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, letterSpacing: 0.3 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PP Progress | Activity | Skill Radar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 1fr", gap: 18 }}>
        <div className="card fu d2" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
            <span className="accent-text">◈</span> {lang === "ru" ? "Лучшие скоры PP" : "Best Scores PP"}
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 14 }}>
            {lang === "ru" ? "PP ваших лучших игр по датам" : "PP of your top plays over time"}
          </div>
          {(() => {
            const sorted = [...bestAll]
              .filter(pl => pl.timestamp)
              .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
              .map(pl => ({
                date: new Date(pl.timestamp).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-US", { month: "short", day: "numeric" }),
                pp:   pl.pp,
                map:  pl.map,
              }));
            if (!sorted.length) return (
              <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 28, opacity: 0.4 }}>◈</div>
                <span style={{ color: "var(--muted)", fontSize: 13 }}>
                  {lang === "ru" ? "Нет топ-плеев" : "No top plays yet"}
                </span>
              </div>
            );
            return (
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={sorted}>
                  <defs>
                    <linearGradient id="ppLineGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--a)" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="var(--a)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "var(--muted)", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis dataKey="pp" domain={["auto", "auto"]} tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip
                    contentStyle={{ background: "var(--card2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 12 }}
                    formatter={(v, _name, props) => [`${props.payload.pp}pp`, props.payload.map]}
                    labelFormatter={() => ""}
                  />
                  <Line
                    type="monotone"
                    dataKey="pp"
                    stroke="var(--a)"
                    strokeWidth={2}
                    dot={{ fill: "var(--a)", stroke: "var(--card2)", strokeWidth: 2, r: 6 }}
                    activeDot={{ fill: "var(--a)", stroke: "var(--bg)", strokeWidth: 2, r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            );
          })()}
        </div>

        <ActivityCard p={p} lang={lang} />

        <div className="card fu d3" style={{ padding: 22 }}>
          <div style={{ fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
            <span className="accent-text">◉</span> {dt.skillRadar}
          </div>
          {skills.length > 0 ? (
            <ResponsiveContainer width="100%" height={170}>
              <RadarChart data={skills}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey={lang === "ru" ? "skillRu" : "skill"} tick={{ fill: "var(--muted)", fontSize: 11 }} />
                <Radar dataKey="value" stroke="var(--a)" fill="var(--dim)" strokeWidth={2} />
                <Tooltip contentStyle={{ background: "var(--card2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)" }} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 28, opacity: 0.4 }}>◉</div>
              <span style={{ color: "var(--muted)", fontSize: 13 }}>
                {lang === "ru" ? "Нет данных для радара" : "No skill data available"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Grades + Plays ── */}
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 18 }}>
        <div className="card fu d3" style={{ padding: 22, minWidth: 190 }}>
          <div style={{ fontWeight: 700, marginBottom: 18, fontSize: 14 }}>
            <span className="accent-text">★</span> {dt.grades}
          </div>
          {p.grades
            ? Object.entries(p.grades).map(([g, cnt]) => (
                <div key={g} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span className={`rank-badge rank-${g}`}>{g}</span>
                  <div className="pb-wrap" style={{ flex: 1 }}>
                    <div className="pb" style={{ width: `${Math.min(100, cnt / 7)}%` }} />
                  </div>
                  <span className="mono" style={{ fontSize: 12, minWidth: 28, textAlign: "right" }}>{cnt}</span>
                </div>
              ))
            : <div style={{ fontSize: 12, color: 'var(--muted)' }}>No grades found</div>}
        </div>

        <div className="card fu d4" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { id: "recent", icon: "▶", en: "Recent Plays", ru: "Последние" },
                { id: "best",   icon: "★", en: "Best Plays",   ru: "Лучшие"   },
              ].map(tab => (
                <button key={tab.id} onClick={() => setPlaysTab(tab.id)} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                  border: "1px solid",
                  borderColor: playsTab === tab.id ? "var(--a)" : "var(--border)",
                  background:  playsTab === tab.id ? "var(--dim)" : "transparent",
                  color:       playsTab === tab.id ? "var(--a)"  : "var(--muted)",
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
                }}>
                  <span>{tab.icon}</span>
                  {lang === "ru" ? tab.ru : tab.en}
                </button>
              ))}
            </div>

            {playsTab === "recent" && recentHasMore && (
              <button onClick={() => setRecentExpanded(v => !v)} className="btn-ghost"
                style={{ padding: "3px 14px", fontSize: 11, borderRadius: 20 }}>
                {recentExpanded
                  ? (lang === "ru" ? "▲ Свернуть" : "▲ Collapse")
                  : `▼ +${recentGroups.length - 3} ${lang === "ru" ? "ещё" : "more"}`}
              </button>
            )}
            {playsTab === "best" && bestHasMore && (
              <button onClick={() => setBestExpanded(v => !v)} className="btn-ghost"
                style={{ padding: "3px 14px", fontSize: 11, borderRadius: 20 }}>
                {bestExpanded
                  ? (lang === "ru" ? "▲ Свернуть" : "▲ Collapse")
                  : `▼ +${bestAll.length - 3} ${lang === "ru" ? "ещё" : "more"}`}
              </button>
            )}
          </div>

          {/* RECENT */}
          {playsTab === "recent" && (
            recentGroups.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--muted)', padding: "20px 0", textAlign: "center" }}>
                  {lang === "ru" ? "Нет недавних игр" : "No recent activity"}
                </div>
              : <div>

                  {visibleGroups.map((group, i) => (
                    <PlayGroup
                      key={group.mapName + group.diff + i}
                      group={group}
                      lang={lang}
                      isLast={i === visibleGroups.length - 1}
                      bestPlays={bestAll}
                    />
                  ))}
                </div>
          )}

          {/* BEST PLAYS */}
          {playsTab === "best" && (
            bestAll.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--muted)', padding: "20px 0", textAlign: "center" }}>
                  {lang === "ru" ? "Нет лучших игр" : "No best plays found"}
                </div>
              : visibleBest.map((r, i) => {
                  // Weighted PP для best plays: позиция i в списке
                  const weighted = Math.round(r.pp * Math.pow(0.95, i));
                  return (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "9px 0",
                      borderBottom: i < visibleBest.length - 1 ? "1px solid var(--border)" : "none",
                    }}>
                      <span className={`rank-badge rank-${r.rank}`}>{r.rank}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {r.map}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {r.diff && <span>{r.diff}</span>}
                          {r.diff && <span>·</span>}
                          <span>{r.acc}%</span>
                          {r.stars > 0 && <span style={{ color: "#ffb6d0" }}>✦ {r.stars}★</span>}
                          <ModChips mods={r.mods} />
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        {r.pp > 0 && (
                          <PPDisplay pp={r.pp} ppInfo={{ weighted, raw: r.pp }} size={13} />
                        )}
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{r.date}</div>
                      </div>
                    </div>
                  );
                })
          )}
        </div>
      </div>

      <QueueSection lang={lang} />
    </div>
  );
};

export default Dashboard;